import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import { StyledAuthContainer } from "../../components/micro/micro.styled";
import { api } from "../../api/base";

export const ArrangePage = () => {
  const [inputFile, setInputFile] = useState<File | null>(null);
  const [library, setLibrary] = useState<string>("variety");
  const [isPreparing, setIsPreparing] = useState<boolean>(false);
  const [prepareResult, setPrepareResult] = useState<any>(null);

  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadResult, setDownloadResult] = useState<any>(null);

  const handleInputFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newInputFile = e.target.files[0];
      if (newInputFile !== inputFile) {
        setInputFile(newInputFile);
        setPrepareResult(null);
      }
    }
  };

  const handleLibraryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLibrary((e.target as HTMLInputElement).value);
  };

  const handleUpload = async () => {
    if (!inputFile) {
      return;
    }

    setIsPreparing(true);
    setDownloadResult(null);
    try {
      const formData = new FormData();
      formData.append("file", inputFile);

      const response = await api.postForm(
        `arrange/prepare/${library}`,
        formData,
      );
      console.log(response.data);

      setPrepareResult(response.data);
    } catch (err: any) {
      console.log(err);

      var responseError = err?.response?.data;
      if (!responseError?.error) {
        responseError = { error: `Internal error occurred: ${err}` };
      }

      setPrepareResult(responseError);
    } finally {
      setIsPreparing(false);
    }
  };

  const handleDownloadInternal = async (rankIndex: number) => {
    if (!inputFile || !prepareResult?.ranks) {
      return;
    }

    const templateLibrary = prepareResult.ranks[rankIndex]?.library;
    const templateIndex = prepareResult.ranks[rankIndex]?.template;
    const templatePlan = prepareResult.ranks[rankIndex]?.plan;
    if (templateIndex === undefined || templatePlan === undefined) {
      return;
    }

    const planBlob = new Blob([JSON.stringify(templatePlan)], {
      type: "application/json",
    });

    setIsDownloading(true);
    try {
      const formData = new FormData();
      formData.set("file", inputFile);
      formData.set("plan", planBlob);

      const response = await api.postForm(
        `arrange/download/${templateLibrary}/${templateIndex}`,
        formData,
        { responseType: "blob" },
      );

      if (response.data) {
        const inputFileName = inputFile.name;
        const inputFileBaseName =
          inputFileName.substring(0, inputFileName.lastIndexOf(".")) ||
          inputFileName;
        const generatedName = `${inputFileBaseName}_Variation_${rankIndex + 1}.als`;

        var url = window.URL.createObjectURL(response.data);
        var a = document.createElement("a");
        a.href = url;
        a.download = generatedName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      setDownloadResult(null);
    } catch (err: any) {
      console.log(err);

      var responseError = err?.response?.data;
      if (!responseError?.error) {
        responseError = { error: "Internal error occurred" };
      }

      setDownloadResult(responseError);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownload = (rankIndex: number) => {
    return async () => {
      await handleDownloadInternal(rankIndex);
    };
  };

  const DownloadButton = (index: number) => {
    const versionNumber = index + 1;

    return (
      <>
        {!isPreparing && prepareResult?.ranks?.length > index && (
          <>
            <Box
              mt={3}
              display={"flex"}
              alignItems={"center"}
              gap={"30%"}
              mb={2}
            >
              <Button
                onClick={handleDownload(index)}
                variant={"outlined"}
                disabled={isDownloading}
              >
                Download Version {versionNumber}
              </Button>
            </Box>
            <div>
              <details>
                <summary>Version {versionNumber} Details</summary>
                <pre>
                  <code>
                    {JSON.stringify(prepareResult.ranks[index], null, 2)}
                  </code>
                </pre>
              </details>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <StyledAuthContainer>
      <Box
        gap={"20px"}
        justifyContent={"center"}
        alignItems={"center"}
        flexDirection={"column"}
        display={"flex"}
        height={"30%"}
        maxWidth={"427px"}
        width={"100%"}
        mx={"auto"}
      >
        <input
          id="file"
          type="file"
          accept=".als"
          onChange={handleInputFileChange}
        />
        {inputFile && (
          <>
            <Button
              onClick={handleUpload}
              variant={"outlined"}
              disabled={isPreparing}
            >
              Arrange
            </Button>
            <FormControl disabled={isPreparing}>
              <FormLabel id="library-label">Training Library</FormLabel>
              <RadioGroup
                row
                name="library-group"
                value={library}
                onChange={handleLibraryChange}
              >
                <FormControlLabel
                  value="variety"
                  control={<Radio />}
                  label="Variety"
                />
                <FormControlLabel
                  value="jason"
                  control={<Radio />}
                  label="Jason"
                />
              </RadioGroup>
            </FormControl>
          </>
        )}
      </Box>
      <p></p>
      {isPreparing && (
        <Typography fontSize={12} color={"primary"} variant={"body1"}>
          Preparing...
        </Typography>
      )}
      {!isPreparing && prepareResult?.error && (
        <Typography fontSize={12} color={"error"} variant={"body1"}>
          {prepareResult?.error}
        </Typography>
      )}
      {!isPreparing && prepareResult?.info && !prepareResult?.ranks?.length && (
        <Typography fontSize={12} color={"error"} variant={"body1"}>
          No arrangement found
        </Typography>
      )}
      {!isDownloading && downloadResult?.error && (
        <Typography fontSize={12} color={"error"} variant={"body1"}>
          {downloadResult?.error}
        </Typography>
      )}
      {DownloadButton(0)}
      {DownloadButton(1)}
      {DownloadButton(2)}
    </StyledAuthContainer>
  );
};
